import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';

/** Small local stand-ins for the web app's shared Button/Badge/Card. */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'outline' | 'danger';
};

/** The admin's design-token button (Redesign-D): brand primary, line outline, danger. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', className = '', ...props },
  ref,
) {
  const base =
    'inline-flex h-10 items-center justify-center rounded-cf-md px-4 text-[14px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink disabled:cursor-not-allowed disabled:opacity-40';
  const styles =
    variant === 'outline'
      ? 'border border-cf-line bg-cf-surface text-cf-ink hover:border-cf-ink'
      : variant === 'danger'
        ? 'bg-cf-danger text-white hover:opacity-90'
        : 'bg-cf-brand text-white hover:bg-cf-brand-hover';
  return <button ref={ref} className={`${base} ${styles} ${className}`} {...props} />;
});

type BadgeVariant = 'success' | 'destructive' | 'warning' | 'secondary';

const BADGE_STYLES: Record<BadgeVariant, string> = {
  success: 'bg-green-100 text-green-800',
  destructive: 'bg-red-100 text-red-800',
  warning: 'bg-orange-100 text-orange-800',
  secondary: 'bg-yellow-100 text-yellow-800',
};

export function Badge({ variant, children }: { variant: BadgeVariant; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${BADGE_STYLES[variant]}`}
    >
      {children}
    </span>
  );
}

export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`rounded-lg border border-gray-200 bg-white shadow-sm ${className}`}>
      {children}
    </div>
  );
}
